import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import {
  MapContainer,
  TileLayer,
  GeoJSON,
  LayersControl,
  ScaleControl,
} from "react-leaflet";
import { FaCircleInfo } from "react-icons/fa6";
import "leaflet/dist/leaflet.css";
import L from "leaflet"; // Import the Leaflet library
import { MapHomeControl, MapLegendControl } from "../../../../common/MapControls/MapControls";

// 1. Define the color mapping for your soil textures
const textureColorMap = {
  Clay: "#d73027",
  "Clay Loam": "#f46d43",
  Loam: "#fdae61",
  Organic: "#fee090",
  Sand: "#ffffbf",
  "Sandy Clay": "#e0f3f8",
  "Sandy Loam": "#abd9e9",
  "Silty Clay": "#74add1",
  "Silty Loam": "#4575b4",
  Variable: "#313695",
  Default: "#cccccc",
};

// The INITIAL_VIEW_STATE is no longer needed since we will use bounds.
// const INITIAL_VIEW_STATE = {
//   longitude: 125.7275,
//   latitude: -8.8742,
//   zoom: 8,
// };

const InfoButton = ({ onClick }) => {
  // Leaflet's own drag/click handling swallows clicks on overlay controls
  // unless propagation to the map is explicitly disabled on the DOM node.
  const btnRef = useCallback((node) => {
    if (node) {
      L.DomEvent.disableClickPropagation(node);
      L.DomEvent.disableScrollPropagation(node);
    }
  }, []);
  return (
    <div
      ref={btnRef}
      style={{
        position: "absolute",
        bottom: "20px",
        left: "12px",
        padding: "8px",
        borderRadius: "8px",
        fontSize: "15px",
        cursor: "pointer",
        background: "rgba(255, 255, 255, 0.9)",
        backdropFilter: "blur(10px) brightness(1.1)",
        WebkitBackdropFilter: "blur(10px) brightness(1.1)",
        border: "1px solid rgba(0, 0, 0, 0.08)",
        color: "#333333",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
        transition: "all 0.2s ease",
        zIndex: 999,
      }}
      onClick={onClick}
    >
      <FaCircleInfo />
    </div>
  );
};

const Legend = ({ colorMap, groupedData }) => {
  if (Object.keys(colorMap).length === 0) {
    return null;
  }

  const relevantCategories = Object.entries(colorMap).filter(([name]) => {
    return name !== "Default" || groupedData["Default"];
  });
  const items = relevantCategories.map(([name, color]) => ({ label: name, color }));

  return <MapLegendControl sections={[{ shape: "square", items }]} />;
};

const SoilTexture = () => {
  const [boundaryData, setBoundaryData] = useState(null);
  const [soilTexture, setSoilTexture] = useState(null);
  const [soilTextureFeature, setSoilTextureFeature] = useState(null);
  const [info, setInfo] = useState(false);
  const infoRef = useRef(null);
  const infoPanelRef = useCallback((node) => {
    infoRef.current = node;
    if (node) {
      L.DomEvent.disableClickPropagation(node);
      L.DomEvent.disableScrollPropagation(node);
    }
  }, []);
  useEffect(() => {
    if (!info) return;
    const handleClickOutside = (e) => {
      if (infoRef.current && !infoRef.current.contains(e.target)) setInfo(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [info]);
  const [mapBounds, setMapBounds] = useState(null); // State for map bounds

  useEffect(() => {
    // Fetch both GeoJSON files
    Promise.all([
      fetch("/DATA/OSMbased_adm1_boundary.json").then((res) => res.json()),
      fetch("/Earthquake/soil_texture.geojson").then((res) => res.json()),
    ])
    .then(([boundary, soilData]) => {
      setBoundaryData(boundary);
      setSoilTexture(soilData);

      // --- NEW LOGIC: Calculate and set map bounds from the soil data ---
      if (soilData && soilData.features.length > 0) {
        const geoJsonLayer = L.geoJSON(soilData);
        setMapBounds(geoJsonLayer.getBounds());
      }
    })
    .catch((error) => console.error("Error loading GeoJSON data:", error));
  }, []);

  const groupedTextures = useMemo(() => {
    if (!soilTexture) return {};
    return soilTexture.features.reduce((acc, feature) => {
      const category = feature.properties.texture || "Default";
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(feature);
      return acc;
    }, {});
  }, [soilTexture]);

  const getSoilStyle = (feature) => {
    const category = feature.properties.texture || "Default";
    return {
      color: "black",
      weight: 1,
      opacity: 0.5,
      fillOpacity: 0.8,
      fillColor: textureColorMap[category] || textureColorMap["Default"],
    };
  };

  const onEachFeature = (feature, layer) => {
    layer.on({
      mouseover: (e) => {
        e.target.setStyle({
          weight: 3,
          color: "#666",
          dashArray: "",
          fillOpacity: 0.9,
        });
        setSoilTextureFeature(
          ` <table className="table-popup"> <tbody>
                    ${feature.properties.description}
                    </tbody> </table>`
        );
      },
      mouseout: (e) => {
        e.target.setStyle({
          weight: 1,
          color: "black",
          dashArray: "",
          fillOpacity: 0.8,
        });
        setSoilTextureFeature(null);
      },
      click: (e) => {
        e.target._map.fitBounds(e.target.getBounds());
      },
    });
  };

  return (
    <div className="wind-speed-cyclone-container">
      <div className="map-container-WindSpeedCyclone">
        {/* The MapContainer is now conditionally rendered when mapBounds is available */}
        {mapBounds && (
          <MapContainer
            bounds={mapBounds} // Use the calculated bounds
            attributionControl={false}
            style={{ width: "100%", height: "650px" }}
          >
            <MapHomeControl />
            {info && (
              <div
                ref={infoPanelRef}
                style={{
                position: "absolute",
                bottom: "70px",
                left: "12px",
                fontSize: "12px",
                zIndex: 1000,
                padding: "10px 14px",
                borderRadius: "10px",
                background: "rgba(255, 255, 255, 0.9)",
                backdropFilter: "blur(10px) brightness(1.1)",
                WebkitBackdropFilter: "blur(10px) brightness(1.1)",
                border: "1px solid rgba(0, 0, 0, 0.08)",
                boxShadow: "0 4px 16px rgba(0, 0, 0, 0.15), inset 0 1px 2px rgba(255, 255, 255, 0.5)",
                color: "#333333",
                fontWeight: "500",
                maxWidth: "320px",
                lineHeight: "1.4",
              }}
              >
                This page displays static Soil Texture data with multiple
                parameters.
              </div>
            )}
            <InfoButton onClick={() => setInfo(!info)} />
            <LayersControl position="topright">
              <LayersControl.BaseLayer name="White Theme" checked>
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
              </LayersControl.BaseLayer>

              {Object.entries(groupedTextures).map(([category, features]) => (
                <LayersControl.Overlay key={category} name={category} checked>
                  <GeoJSON
                    data={{ type: "FeatureCollection", features: features }}
                    style={getSoilStyle}
                    onEachFeature={onEachFeature}
                  />
                </LayersControl.Overlay>
              ))}
            </LayersControl>

            {soilTextureFeature && (
              <div
                className="hover-box"
                dangerouslySetInnerHTML={{ __html: soilTextureFeature }}
              />
            )}

            {boundaryData && (
              <GeoJSON
                data={boundaryData}
                style={{
                  color: "#000000",
                  weight: 1,
                  opacity: 0.8,
                  dashArray: 6,
                  fillOpacity: 0,
                }}
              />
            )}
            <ScaleControl position="bottomleft" />
            <Legend
              colorMap={textureColorMap}
              groupedData={groupedTextures}
            />
          </MapContainer>
        )}
      </div>
    </div>
  );
};

export default SoilTexture;